import { useState, useEffect } from "react";
import { Package, MessageSquare, TrendingUp, Sparkles, Moon, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_products: 0,
    healing_count: 0,
    raw_count: 0,
    total_inquiries: 0,
    unread_inquiries: 0
  });
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => {
    const auth = sessionStorage.getItem("adminAuth");
    return {
      "Authorization": `Basic ${auth}`,
      "Content-Type": "application/json"
    };
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedProducts = async () => {
    try {
      const response = await fetch(`${API}/admin/seed`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      const data = await response.json();
      toast.success(data.message + " ✨");
      fetchStats();
    } catch (error) {
      toast.error("Failed to seed products");
    }
  };

  const statCards = [
    {
      title: "Total Products",
      value: stats.total_products,
      icon: <Package className="w-5 h-5" />,
      color: "text-mystic-amethyst",
      bgColor: "bg-mystic-amethyst/20"
    },
    {
      title: "Healing Crystals",
      value: stats.healing_count,
      icon: <Star className="w-5 h-5" />,
      color: "text-mystic-gold",
      bgColor: "bg-mystic-gold/20"
    },
    {
      title: "Raw Stones",
      value: stats.raw_count,
      icon: <Moon className="w-5 h-5" />,
      color: "text-mystic-cyan",
      bgColor: "bg-mystic-cyan/20"
    },
    {
      title: "Inquiries",
      value: stats.total_inquiries,
      icon: <MessageSquare className="w-5 h-5" />,
      color: "text-mystic-orchid",
      bgColor: "bg-mystic-orchid/20",
      badge: stats.unread_inquiries > 0 ? stats.unread_inquiries : null
    }
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="glass border-white/10 hover:border-mystic-amethyst/30 transition-all">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-mystic-mist mb-1">{stat.title}</p>
                    <p className="text-3xl font-semibold text-mystic-silver">
                      {loading ? "..." : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-full ${stat.bgColor} flex items-center justify-center ${stat.color} relative`}>
                    {stat.icon}
                    {stat.badge && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-mystic-orchid text-white text-xs rounded-full flex items-center justify-center">
                        {stat.badge}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-mystic-silver italic">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <Link to="/admin/products" data-testid="quick-action-products">
                <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg neon-glow-hover">
                  <Package className="w-4 h-4 mr-2" />
                  Manage Products
                </Button>
              </Link>
              <Link to="/admin/inquiries" data-testid="quick-action-inquiries">
                <Button 
                  variant="outline" 
                  className="border-mystic-amethyst/50 text-mystic-amethyst hover:bg-mystic-amethyst/20 rounded-lg"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  View Inquiries
                  {stats.unread_inquiries > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-mystic-orchid text-white text-xs rounded-full">
                      {stats.unread_inquiries} new
                    </span>
                  )}
                </Button>
              </Link>
            </div>

            {stats.total_products === 0 && (
              <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="text-mystic-mist mb-3">No products yet? Add some sample products to get started.</p>
                <Button 
                  onClick={handleSeedProducts}
                  variant="outline"
                  className="border-mystic-gold/50 text-mystic-gold hover:bg-mystic-gold/20 rounded-lg"
                  data-testid="seed-products-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Add Sample Products
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="glass border-white/10">
          <CardContent className="pt-6">
            <h3 className="font-serif text-lg text-mystic-silver mb-3 italic flex items-center gap-2">
              <Moon className="w-5 h-5 text-mystic-gold" />
              Getting Started Tips
            </h3>
            <ul className="space-y-2 text-sm text-mystic-mist">
              <li className="flex items-start gap-2">
                <span className="text-mystic-amethyst mt-0.5">✧</span>
                Add your products with high-quality mystical images for better conversions
              </li>
              <li className="flex items-start gap-2">
                <span className="text-mystic-amethyst mt-0.5">✧</span>
                Mark your best sellers as "Featured" to showcase them on the homepage
              </li>
              <li className="flex items-start gap-2">
                <span className="text-mystic-amethyst mt-0.5">✧</span>
                Respond to inquiries promptly to build spiritual trust
              </li>
              <li className="flex items-start gap-2">
                <span className="text-mystic-amethyst mt-0.5">✧</span>
                Keep your stock updated to avoid seeker disappointment
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
